import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Trash2, RotateCcw } from 'lucide-react'

const AD_SOURCES = ['Admob', 'Meta', 'Unity', 'AppLovin', 'IronSource', 'Vungle', 'Pangle', 'Mintegral']

/**
 * PAM Waterfall 编辑弹窗
 * 编辑单个中介组的 waterfall 价格
 */
function PamWaterfallEditModal({ isOpen, group, onClose, onSave }) {
  const [waterfallItems, setWaterfallItems] = useState([])
  const [removedItems, setRemovedItems] = useState([])

  useEffect(() => {
    if (group) {
      const currentWaterfall = group.draft || group.waterfall || []
      setWaterfallItems([...currentWaterfall])
      setRemovedItems([])
    }
  }, [group])

  const handleAddItem = () => {
    setWaterfallItems((prev) => [
      ...prev,
      { id: `new-${Date.now()}`, name: 'Admob', price: 0 },
    ])
  }

  const handleUpdateItem = (index, field, value) => {
    setWaterfallItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    )
  }

  const handleRemoveItem = (index) => {
    const item = waterfallItems[index]
    // If it's an original item (not newly added), track it as removed
    if (!item.id.startsWith('new-')) {
      setRemovedItems((prev) => [...prev, item])
    }
    setWaterfallItems((prev) => prev.filter((_, i) => i !== index))
  }

  const handleRestoreItem = (item) => {
    setRemovedItems((prev) => prev.filter((i) => i.id !== item.id))
    setWaterfallItems((prev) => [...prev, item])
  }

  const handleReset = () => {
    if (group) {
      setWaterfallItems([...(group.waterfall || [])])
      setRemovedItems([])
    }
  }

  const handleSave = () => {
    if (!group) return

    // Sort by price descending
    const sortedWaterfall = waterfallItems
      .filter((item) => item.name && item.price > 0)
      .sort((a, b) => b.price - a.price)

    onSave(group.id, sortedWaterfall)
  }

  const handleClose = () => {
    setWaterfallItems([])
    setRemovedItems([])
    onClose()
  }

  if (!group) return null

  const hasChanges =
    JSON.stringify(waterfallItems) !== JSON.stringify(group.waterfall || []) ||
    removedItems.length > 0

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>编辑 Waterfall - {group.name}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Current Items */}
          <div className="space-y-2">
            {waterfallItems.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                className="flex gap-2 items-center p-3 border rounded-lg bg-white"
              >
                <Select
                  value={item.name}
                  onValueChange={(value) =>
                    handleUpdateItem(index, 'name', value)
                  }
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AD_SOURCES.map((source) => (
                      <SelectItem key={source} value={source}>
                        {source}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.price === 0 ? '' : item.price}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value === '') {
                      handleUpdateItem(index, 'price', 0)
                    } else {
                      const numValue = parseFloat(value)
                      if (!isNaN(numValue)) {
                        handleUpdateItem(index, 'price', numValue)
                      }
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  className="w-[150px]"
                  placeholder="输入价格"
                />

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleRemoveItem(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            {/* Add New Item Button */}
            <Button
              variant="outline"
              onClick={handleAddItem}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              添加 Waterfall 项
            </Button>
          </div>

          {/* Removed Items */}
          {removedItems.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-600">
                已删除项
              </h4>
              {removedItems.map((item, index) => (
                <div
                  key={`removed-${item.id}-${index}`}
                  className="flex gap-2 items-center p-3 border rounded-lg bg-gray-50 opacity-60"
                >
                  <span className="w-[200px] text-sm line-through">
                    {item.name}
                  </span>
                  <span className="w-[150px] text-sm line-through">
                    ${item.price}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleRestoreItem(item)}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={handleReset} disabled={!hasChanges}>
            重置
          </Button>
          <Button variant="default" onClick={handleSave}>
            保存
          </Button>
          <Button variant="destructive" onClick={handleClose}>
            取消
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PamWaterfallEditModal

